import { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight, Check } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

const SESSION_SHOWN_KEY = "upsell_shown_slugs";

/**
 * UpsellPackModal — Détecte si un produit ajouté au panier fait partie d'un Pack,
 * et propose de remplacer l'article individuel par le pack complet à prix réduit.
 *
 * Session-only : ne s'affiche qu'une fois par produit par session (pas de spam retour).
 *
 * Props:
 *   triggerSlug — slug du produit qui vient d'être ajouté (null = fermé)
 *   onClose — callback fermeture
 */
export default function UpsellPackModal({ triggerSlug, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { cart, removeItem, addToCart } = useApp();

  useEffect(() => {
    if (!triggerSlug) { setData(null); return; }
    // Session dedup — ne pas réafficher pour un slug déjà vu cette session
    try {
      const seen = JSON.parse(window.sessionStorage.getItem(SESSION_SHOWN_KEY) || "[]");
      if (seen.includes(triggerSlug)) { onClose?.(); return; }
    } catch {}
    setLoading(true);
    api.get(`/upsell/find-pack?slug=${encodeURIComponent(triggerSlug)}`)
      .then((r) => {
        if (r.data?.pack) {
          setData(r.data);
          // Marquer comme vu pour cette session
          try {
            const seen = JSON.parse(window.sessionStorage.getItem(SESSION_SHOWN_KEY) || "[]");
            window.sessionStorage.setItem(SESSION_SHOWN_KEY, JSON.stringify([...seen, triggerSlug]));
          } catch {}
        } else {
          onClose?.();
        }
      })
      .catch(() => onClose?.())
      .finally(() => setLoading(false));
  }, [triggerSlug, onClose]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!triggerSlug || !data?.pack) return null;

  async function takePack() {
    try {
      // 1. Retirer le produit individuel du panier — match par resource_slug (vrai slug de la ressource)
      const individual = cart.find((it) =>
        it.resource_slug === data.trigger_slug || it.slug === data.trigger_slug
      );
      if (individual) removeItem(individual.product_id);
      // 2. Ajouter le pack avec les bons flags (structure attendue par addToCart)
      addToCart({
        id: `pack-${data.pack.id}`,
        slug: `pack-${data.pack.id}`,
        resource_slug: data.pack.slug,
        is_pack: true,          // évite de re-triggerer un upsell sur le pack lui-même
        is_digital: true,
        no_shipping: true,
        name: { fr: data.pack.titre },
        images: [data.pack.cover],
        price: data.pack.prix_pack,
      }, 1);
      toast.success(`Pack ajouté — vous économisez ${data.savings.toFixed(2)} € !`);
      onClose?.();
    } catch (e) {
      toast.error("Impossible d'ajouter le pack");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[110] bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="upsell-pack-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header festive */}
        <div className="bg-gradient-to-br from-amber-400 via-coral to-pedablue text-white p-5 flex items-start gap-3 relative">
          <Sparkles size={26} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest font-semibold opacity-90">💡 Bonne affaire détectée</p>
            <h2 className="font-display text-2xl font-medium mt-1 leading-tight">
              Prenez le pack complet et économisez <span className="font-bold">{data.savings.toFixed(2)} €</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-full flex-shrink-0"
            aria-label="Fermer"
            data-testid="upsell-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-ink/80 leading-relaxed">
            Le produit que vous venez d'ajouter fait partie du
            {" "}<strong className="text-pedablue">{data.pack.titre}</strong>.
            {" "}Prenez le pack complet <strong>({data.pack.pdf_count} ressources)</strong> pour seulement
            {" "}<strong className="text-coral-dark">{data.pack.prix_pack.toFixed(2)} €</strong>
            {" "}au lieu de <span className="line-through opacity-60">{data.individual_total.toFixed(2)} €</span> !
          </p>

          {/* Covers grid : tous les PDFs du pack côte à côte */}
          <div>
            <p className="text-xs uppercase tracking-widest text-ink-soft mb-2">Ce que contient le pack</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="upsell-covers-grid">
              {(data.covers || []).slice(0, 8).map((c, idx) => (
                <div
                  key={c.slug || idx}
                  className={`aspect-[3/4] rounded-xl overflow-hidden bg-ivory-200 border-2 ${c.slug === data.trigger_slug ? "border-coral shadow-lg" : "border-ink/10"} relative`}
                  title={c.titre}
                >
                  {c.cover ? (
                    <img src={c.cover} alt={c.titre} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-ink-soft p-2 text-center">{c.titre}</div>
                  )}
                  {c.slug === data.trigger_slug && (
                    <span className="absolute top-1 left-1 bg-coral text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                      Le vôtre
                    </span>
                  )}
                </div>
              ))}
            </div>
            {data.covers?.length > 8 && (
              <p className="text-xs text-ink-soft mt-2 text-center">
                + {data.covers.length - 8} autres ressources incluses
              </p>
            )}
          </div>

          {/* Économies visuelles */}
          <div className="bg-sage/10 border border-sage/30 rounded-2xl p-3 flex items-center gap-3">
            <div className="bg-sage-dark text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
              -{Math.round((data.savings / Math.max(data.individual_total, 1)) * 100)}%
            </div>
            <p className="text-sm text-ink">
              Économies : <strong className="text-sage-dark">{data.savings.toFixed(2)} €</strong> · Prix par ressource : <strong>{(data.pack.prix_pack / data.pack.pdf_count).toFixed(2)} €</strong>
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full bg-ink/5 text-ink font-medium hover:bg-ink/10"
              data-testid="upsell-keep-individual"
            >
              Garder uniquement cet article
            </button>
            <button
              onClick={takePack}
              disabled={loading}
              className="flex-[1.4] inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-coral hover:bg-coral-dark text-white font-semibold shadow-md disabled:opacity-60"
              data-testid="upsell-take-pack"
            >
              <Check size={18} /> Prendre le Pack — {data.pack.prix_pack.toFixed(2)} €
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
